import { supabase } from "@/integrations/supabase/client";
import { fetchAllRows } from '@/lib/fetchAllRows';

/**
 * Category-based scoring for auto cover photo selection.
 * Kept in TS for tests and any client-side display; the authoritative
 * selection now runs server-side in the `auto_select_cover_photo(p_cafe_id)`
 * SECURITY DEFINER RPC so non-admin uploaders can trigger a recompute.
 */
const CATEGORY_SCORES: Record<string, number> = {
  ambiance: 5,
  seating: 4,
  workspace: 3,
  exterior: 2,
  outlets: 1,
  food: 0,
  dogs: 0,
};

export function scorePhoto(
  category: string | null,
  categories: string[] | null,
): number {
  const merged = new Set<string>();
  if (category) merged.add(category);
  if (categories) for (const c of categories) if (c) merged.add(c);

  if (merged.size === 0) return 0;

  if (merged.has("ambiance") && merged.has("seating")) return 10;
  if (merged.has("seating") && merged.has("workspace")) return 7;

  if (merged.has("ambiance")) return 8;
  if (merged.has("seating")) return 6;
  if (merged.has("workspace")) return 5;
  if (merged.has("exterior")) return 3;
  if (merged.has("outlets")) return CATEGORY_SCORES.outlets;
  if (merged.has("food")) return 1;

  let score = 0;
  for (const cat of merged) {
    score = Math.max(score, CATEGORY_SCORES[cat] ?? 0);
  }
  return score;
}

/**
 * Delegates cover selection to the SECURITY DEFINER RPC so writes are not
 * silently blocked by cafe_photos / cafes RLS for non-admin uploaders.
 * Fire-and-forget — errors are logged but not thrown.
 */
export async function autoSelectCoverPhoto(cafeId: string): Promise<void> {
  try {
    const { error } = await supabase.rpc("auto_select_cover_photo", {
      p_cafe_id: cafeId,
    });
    if (error) {
      console.error("Auto cover photo selection failed:", error);
    }
  } catch (err) {
    console.error("Auto cover photo selection failed:", err);
  }
}

/**
 * Bulk re-run: iterate active, non-manually-set cafés and call the RPC.
 * Used by the admin ReRunAutoCoverButton.
 */
export async function autoSelectAllCoverPhotos(): Promise<number> {
  try {
    const cafes = await fetchAllRows<{ id: string }>(
      () => supabase
        .from("cafes")
        .select("id")
        .eq("is_active", true)
        .eq("cover_manually_set", false),
      "autoSelectAllCoverPhotos",
    );

    let updated = 0;
    for (const cafe of cafes) {
      const { error: rpcError } = await supabase.rpc("auto_select_cover_photo", {
        p_cafe_id: cafe.id,
      });
      if (!rpcError) updated++;
    }
    return updated;
  } catch (err) {
    console.error("Bulk auto cover selection failed:", err);
    return 0;
  }
}
